import { useState, useRef, useEffect, useCallback } from "react";
import { Portal } from "@chakra-ui/react";
import { FiMoreVertical, FiAlertTriangle } from "react-icons/fi";
import { cn, showToast } from "utils/helpers";
import Modal from "newui/components/ui/Modal/Modal";
import {
	useResendMemberInvite,
	useRevokeMember,
	useUnrevokeMember,
	useSendReminder,
	useDeletePanelMember,
} from "../../hooks/usePanelScoringQueries";
import type { PanelMemberStatus } from "../../types/panel.type";
import { MemberReasonModal } from "./MemberReasonModal";

export interface PanelMemberActionTarget {
	id: number;
	name: string | null;
	email: string;
	status: PanelMemberStatus;
	disclaimerStatus?: "no_conflict" | "conflict" | "reviewed" | null;
}

interface MemberActionsMenuProps {
	evalId: number;
	member: PanelMemberActionTarget;
	rfxId: number;
	/** Called after any action mutates the member, so the parent can refresh
	 * its own data source (the panel-members query is invalidated regardless). */
	onChanged?: () => void;
	/** Called when "Review Conflict" is selected from the actions menu */
	onReviewConflict?: () => void;
	/** Called when "View Conflict" is selected from the actions menu */
	onViewConflict?: () => void;
}

type ActiveModal = "revoke" | "unrevoke" | "delete" | null;

const REVOKABLE_STATUSES: PanelMemberStatus[] = ["accepted", "in_progress", "submitted", "opened"];
const DELETABLE_STATUSES: PanelMemberStatus[] = ["pending", "invited"];
const REMINDABLE_STATUSES: PanelMemberStatus[] = ["invited", "accepted", "opened", "in_progress"];

export const MemberActionsMenu: React.FC<MemberActionsMenuProps> = ({
	member,
	rfxId,
	evalId,
	onChanged,
	onReviewConflict,
	onViewConflict,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [activeModal, setActiveModal] = useState<ActiveModal>(null);
	const [menuPos, setMenuPos] = useState<{ left: number; top: number; }>({ top: 0, left: 0 });
	const menuRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);

	const MENU_WIDTH = 176; // w-44

	const toggleMenu = useCallback(() => {
		setIsOpen((prev) => {
			if (!prev && triggerRef.current) {
				const rect = triggerRef.current.getBoundingClientRect();
				// Right-align the menu to the trigger, just below it. Fixed
				// positioning + a portal keeps it clear of the table's overflow
				// clipping and any sibling stacking contexts.
				setMenuPos({
					top: rect.bottom + 4,
					left: Math.max(8, rect.right - MENU_WIDTH),
				});
			}
			return !prev;
		});
	}, []);

	const resendInviteMutation = useResendMemberInvite(rfxId, evalId);
	const revokeMutation = useRevokeMember(rfxId, evalId);
	const unrevokeMutation = useUnrevokeMember(rfxId, evalId);
	const reminderMutation = useSendReminder(rfxId, evalId);
	const deleteMutation = useDeletePanelMember(rfxId, evalId);

	const displayName = member.name || member.email;

	const canResendInvite = member.status === "invited";
	const canRevoke = REVOKABLE_STATUSES.includes(member.status);
	const canUnrevoke = member.status === "revoked";
	const canDelete = DELETABLE_STATUSES.includes(member.status);
	const canSendReminder = REMINDABLE_STATUSES.includes(member.status);
	const canReviewConflict = member.disclaimerStatus === "conflict" && !!onReviewConflict;
	const canViewConflict = member.disclaimerStatus === "reviewed" && !!onViewConflict;
	const hasAnyAction = canResendInvite || canRevoke || canUnrevoke || canDelete || canSendReminder || canReviewConflict || canViewConflict;

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent): void => {
			const target = event.target as Node;
			const inMenu = menuRef.current?.contains(target);
			const inTrigger = triggerRef.current?.contains(target);
			if (!inMenu && !inTrigger) {
				setIsOpen(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	const afterChange = useCallback(() => {
		setActiveModal(null);
		onChanged?.();
	}, [onChanged]);

	const handleResendInvite = useCallback(() => {
		setIsOpen(false);
		resendInviteMutation.mutate(member.id, {
			onSuccess: () => {
				showToast("Invitation resent successfully.", "Invite Resent", true);
				afterChange();
			},
			onError: () => showToast("Failed to resend invitation.", "Error", false),
		});
	}, [member.id, resendInviteMutation, afterChange]);

	const handleSendReminder = useCallback(() => {
		setIsOpen(false);
		reminderMutation.mutate(member.id, {
			onSuccess: () => {
				showToast("Reminder sent successfully.", "Reminder Sent", true);
				afterChange();
			},
			onError: () => showToast("Failed to send reminder.", "Error", false),
		});
	}, [member.id, reminderMutation, afterChange]);

	const handleConfirmRevoke = useCallback((reason: string) => {
		revokeMutation.mutate({ memberId: member.id, reason }, {
			onSuccess: () => {
				showToast("Member access has been revoked.", "Access Revoked", true);
				afterChange();
			},
			onError: () => showToast("Failed to revoke access.", "Error", false),
		});
	}, [member.id, revokeMutation, afterChange]);

	const handleConfirmUnrevoke = useCallback((reason: string) => {
		unrevokeMutation.mutate({ memberId: member.id, reason }, {
			onSuccess: () => {
				showToast("Member access has been restored.", "Access Restored", true);
				afterChange();
			},
			onError: () => showToast("Failed to restore access.", "Error", false),
		});
	}, [member.id, unrevokeMutation, afterChange]);

	const handleConfirmDelete = useCallback(() => {
		deleteMutation.mutate(member.id, {
			onSuccess: () => {
				showToast("Panel member removed.", "Member Removed", true);
				afterChange();
			},
			onError: () => showToast("Failed to remove panel member.", "Error", false),
		});
	}, [member.id, deleteMutation, afterChange]);

	const menuItemClass = "flex w-full items-center px-3 py-2 text-left text-sm transition-colors text-gray-700 hover:bg-gray-50";

	return (
		<>
			<div className="relative">
				<button
					ref={triggerRef}
					type="button"
					onClick={toggleMenu}
					className="inline-flex items-center justify-center rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0031DD] focus-visible:ring-offset-1"
					aria-label={`Actions for ${displayName}`}
					aria-haspopup="true"
					aria-expanded={isOpen}
					data-testid={`member-actions-trigger-${member.id}`}
				>
					<FiMoreVertical size={16} />
				</button>
				{isOpen && (
					<Portal>
						<div
							ref={menuRef}
							style={{ position: "fixed", top: menuPos.top, left: menuPos.left, width: MENU_WIDTH }}
							className="z-[1400] rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
							role="menu"
							data-testid={`member-actions-menu-${member.id}`}
						>
							{canResendInvite && (
								<button
									type="button"
									role="menuitem"
									onClick={handleResendInvite}
									className={menuItemClass}
									data-testid={`member-action-resend-${member.id}`}
								>
									Resend Invite
								</button>
							)}
							{canReviewConflict && (
								<button
									type="button"
									role="menuitem"
									onClick={() => { setIsOpen(false); onReviewConflict?.(); }}
									className="flex w-full items-center px-3 py-2 text-left text-sm text-amber-600 hover:bg-amber-50 transition-colors"
									data-testid={`member-action-review-conflict-${member.id}`}
								>
									Review Conflict
								</button>
							)}
							{canViewConflict && (
								<button
									type="button"
									role="menuitem"
									onClick={() => { setIsOpen(false); onViewConflict?.(); }}
									className={menuItemClass}
									data-testid={`member-action-view-conflict-${member.id}`}
								>
									View Conflict Review
								</button>
							)}
							{canRevoke && (
								<button
									type="button"
									role="menuitem"
									onClick={() => { setIsOpen(false); setActiveModal("revoke"); }}
									className="flex w-full items-center px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors"
									data-testid={`member-action-revoke-${member.id}`}
								>
									Revoke Access
								</button>
							)}
							{canUnrevoke && (
								<button
									type="button"
									role="menuitem"
									onClick={() => { setIsOpen(false); setActiveModal("unrevoke"); }}
									className="flex w-full items-center px-3 py-2 text-left text-sm text-green-600 hover:bg-green-50 transition-colors"
									data-testid={`member-action-unrevoke-${member.id}`}
								>
									Unrevoke Access
								</button>
							)}
							{canSendReminder && (
								<button
									type="button"
									role="menuitem"
									onClick={handleSendReminder}
									className={menuItemClass}
									data-testid={`member-action-remind-${member.id}`}
								>
									Send Reminder
								</button>
							)}
							{canDelete && (
								<button
									type="button"
									role="menuitem"
									onClick={() => { setIsOpen(false); setActiveModal("delete"); }}
									className="flex w-full items-center px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors"
									data-testid={`member-action-delete-${member.id}`}
								>
									Delete
								</button>
							)}
							{!hasAnyAction && (
								<span className="flex w-full items-center px-3 py-2 text-left text-sm text-gray-400">
									No actions available
								</span>
							)}
						</div>
					</Portal>
				)}
			</div>

			<MemberReasonModal
				isOpen={activeModal === "revoke"}
				title="Revoke Member Access"
				confirmLabel="Revoke Access"
				reasonLabel="Reason for revoking access"
				isSubmitting={revokeMutation.isPending}
				onConfirm={handleConfirmRevoke}
				onClose={() => setActiveModal(null)}
				body={(
					<div className="space-y-3">
						<p className="text-sm text-gray-600">
							Are you sure you want to revoke access for <strong>{displayName}</strong>?
						</p>
						<div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
							<div className="flex gap-2">
								<FiAlertTriangle size={16} className="shrink-0 text-amber-600 mt-0.5" aria-hidden="true" />
								<div className="text-sm text-amber-700 space-y-1">
									<p>This member will no longer be able to submit or modify their scores.</p>
									<p>Any scores they have already entered will be excluded from the evaluation averages and comparison data.</p>
								</div>
							</div>
						</div>
					</div>
				)}
			/>

			<MemberReasonModal
				isOpen={activeModal === "unrevoke"}
				title="Unrevoke Member Access"
				confirmLabel="Restore Access"
				reasonLabel="Reason for restoring access"
				isSubmitting={unrevokeMutation.isPending}
				onConfirm={handleConfirmUnrevoke}
				onClose={() => setActiveModal(null)}
				body={(
					<p className="text-sm text-gray-600">
						<strong>{displayName}</strong> will be re-invited to participate and restored to
						their previous status so they can continue scoring.
					</p>
				)}
			/>

			{activeModal === "delete" && (
				<Modal
					isOpen
					onClose={() => setActiveModal(null)}
					title="Remove Panel Member"
					size="sm"
					iconType="error"
					modalType="standard"
					closeOnEsc
					closeOnOverlayClick
					primaryButton={{
						text: "Remove",
						onClick: handleConfirmDelete,
						width: "full",
						disabled: deleteMutation.isPending,
					}}
					secondaryButton={{
						text: "Cancel",
						onClick: () => setActiveModal(null),
						width: "full",
					}}
				>
					<p className="text-sm text-gray-600 text-center">
						Are you sure you want to remove <strong>{displayName}</strong> from the evaluation panel?
					</p>
				</Modal>
			)}
		</>
	);
};
